import React, { useEffect, useState } from 'react';
import axios from '../axios';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppDispatch, RootState } from '../store';
import { getCurrentUser } from '../store/authSlice';
import { useToast } from '../components/ToastProvider';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const { user } = useSelector((state: RootState) => state.auth);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPhoneNumber(user?.phoneNumber || '');
    setEmailNotificationsEnabled(user?.emailNotificationsEnabled ?? true);
  }, [user?.phoneNumber, user?.emailNotificationsEnabled]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.patch('/api/auth/me', {
        phoneNumber: phoneNumber.trim() || null,
        emailNotificationsEnabled,
      });
      await dispatch(getCurrentUser()).unwrap();
      toast.show(t('settings.saved') as string, 'success');
    } catch (error: any) {
      toast.show(String(error.response?.data || t('settings.saveError')), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mx-auto max-w-2xl">
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-[#111D4E]">{t('settings.title')}</h1>
      <form onSubmit={handleSubmit} className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div className="text-sm font-semibold text-[#111D4E]">{t('settings.profileTitle')}</div>
          <div className="text-xs text-gray-600">{t('settings.partnerProfileSubtitle', t('settings.profileSubtitle'))}</div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-[#111D4E]">{t('auth.email')}</label>
            <input value={user?.email || ''} disabled className="w-full px-3 py-2 border border-slate-200 rounded-md bg-slate-50 text-gray-600" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-[#111D4E]">{t('settings.phone')}</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md"
              placeholder={t('settings.phonePlaceholder') as string}
            />
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <label className="flex items-start gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={emailNotificationsEnabled}
                onChange={(e) => setEmailNotificationsEnabled(e.target.checked)}
                className="mt-1"
              />
              <span>
                <span className="block font-medium text-[#111D4E]">{t('settings.emailNotifications')}</span>
                <span className="block text-xs text-gray-600">{t('settings.emailNotificationsHint')}</span>
              </span>
            </label>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => {
                setPhoneNumber(user?.phoneNumber || '');
                setEmailNotificationsEnabled(user?.emailNotificationsEnabled ?? true);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md font-semibold"
            >
              {t('settings.reset')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-[#47ABDB] disabled:opacity-60 text-white rounded-md font-semibold hover:bg-[#3d8fb8]"
            >
              {loading ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </div>
      </form>
    </section>
  );
};

export default Settings;
